/**
 * 📅 **12주 수업 계획** — 교재와 화면을 잇는다(FR-CLASS-11).
 *
 * ## 왜 필요한가
 * 방과후 수업은 **12주짜리 상품**인데 화면에는 「주차」라는 개념이 없었다.
 * 그림책 여섯 쪽이 전부라 **3주차에 무엇을 하는지 화면이 모른다.**
 * 선생님이 매번 말로 안내해야 하고, **결석한 아이는 따라잡을 방법이 없다.**
 * 교재가 주 수입인 사업에서 교재와 화면이 안 이어져 있다는 뜻이기도 하다.
 *
 * ## 🔴 「이번 주」는 **선생님이 정한다** — 달력이 아니라
 * 개강일로부터 몇 주 지났는지 세는 편이 손이 덜 가지만, 그러면 **쉰 주를 못 센다.**
 * 방학·행사·휴교로 한 주를 건너뛰면 화면은 4주차인데 교실은 3주차를 하고 있다.
 * 그러면 아이 화면의 안내가 **틀린 것**이 되고, 선생님은 그걸 끄고 싶어진다.
 *
 * 그래서 반마다 **지금 몇 주차인지**를 들고 있고, 선생님이 [다음 주로]로 넘긴다.
 * 달력은 **처음 제안**에만 쓴다(그 반이 열린 지 N주 → 「N주차인가요?」).
 *
 * ## 🔴 계획이 없으면 **아무것도 안 보인다**
 * 운영자가 아직 안 적었으면 주차 안내를 아예 안 띄운다. 빈 칸을 「1주차」로 띄우면
 * 아이 화면에 뜻 없는 줄이 하나 생기고, 선생님은 그걸 없앨 방법이 없다.
 */

export const CLASS_WEEKS_MAX = 12;
export const WEEK_TITLE_MAX = 24;
export const WEEK_DOING_MAX = 80;
export const WEEK_BOOK_MAX = 16;

export interface ClassWeek {
  /** 1..12 */
  n: number;
  /** 이번 주 이름. 예: 「내 친구 만들기」 */
  title: string;
  /** 아이에게 보여 줄 한 줄. 예: 「그림책 1·2쪽을 써 봐요」 */
  doing: string;
  /** 교재 쪽수 — 선생님만 본다. 예: 「12~15쪽」 */
  book?: string;
}

export type ClassPlan = ClassWeek[];

const clip = (v: unknown, max: number): string =>
  typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "";

/**
 * 밖에서 온 계획을 다듬는다(불변식 7).
 *
 * 🔴 **빈 주는 버린다.** 1·2주차만 적고 5주차를 적으면 그 셋만 남는다 —
 * 가운데 빈 칸을 남겨 두면 아이 화면에 빈 안내가 뜬다.
 */
export function sanitizeClassPlan(raw: unknown): ClassPlan {
  if (!Array.isArray(raw)) return [];
  const byWeek = new Map<number, ClassWeek>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const n = Math.trunc(Number(o.n));
    if (!Number.isFinite(n) || n < 1 || n > CLASS_WEEKS_MAX) continue;
    const title = clip(o.title, WEEK_TITLE_MAX);
    const doing = clip(o.doing, WEEK_DOING_MAX);
    const book = clip(o.book, WEEK_BOOK_MAX);
    // 이름도 할 일도 없으면 안 적은 주다
    if (!title && !doing) continue;
    byWeek.set(n, { n, title, doing, ...(book ? { book } : {}) });
  }
  return [...byWeek.values()].sort((a, b) => a.n - b.n);
}

/** 이 반이 지금 몇 주차인가. 다듬어서 1..12 안에 둔다(0 = 안 정함). */
export function sanitizeWeek(raw: unknown): number {
  const n = Math.trunc(Number(raw));
  return Number.isFinite(n) && n >= 1 && n <= CLASS_WEEKS_MAX ? n : 0;
}

/**
 * 반이 열린 날로 **처음 제안**할 주차. 선생님이 아직 안 정했을 때만 쓴다.
 *
 * 🔴 이것을 **자동으로 쓰지 않는다.** 쉰 주를 못 세기 때문이다 — 선생님에게
 * 「N주차인가요?」라고 물어보는 데만 쓴다.
 */
export function suggestWeek(createdAt: string | undefined, now: Date): number {
  if (!createdAt) return 1;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return 1;
  const weeks = Math.floor((now.getTime() - t) / (7 * 86_400_000));
  return Math.min(CLASS_WEEKS_MAX, Math.max(1, weeks + 1));
}

/** 지금 주차의 계획. 없으면 null(계획을 안 적었거나 주차를 안 정했다). */
export function weekOf(plan: ClassPlan, week: number): ClassWeek | null {
  return plan.find((w) => w.n === sanitizeWeek(week)) ?? null;
}

/**
 * 결석한 아이가 따라잡을 것 — **이번 주까지 중 못 한 주**.
 *
 * 🔴 「몇 쪽을 썼나」로는 못 센다. 아이가 3주차에 결석해도 그전 쪽은 차 있기 때문이다.
 * 그래서 **주차 목록**을 그대로 주고, 아이 화면이 「지난 주 것도 이어서 할 수 있어요」로 쓴다.
 */
export function weeksUpTo(plan: ClassPlan, week: number): ClassWeek[] {
  const w = sanitizeWeek(week);
  return w === 0 ? [] : plan.filter((x) => x.n <= w);
}

/** 운영자 화면에 「몇 주를 적었나」. */
export function planSay(plan: ClassPlan): string {
  if (plan.length === 0)
    return "아직 아무 주차도 안 적었어요. 적으면 아이 화면에 이번 주 할 일이 떠요.";
  const missing = [];
  for (let n = 1; n <= plan[plan.length - 1]!.n; n++) {
    if (!plan.some((w) => w.n === n)) missing.push(n);
  }
  const head = `${plan.length}주 적었어요 (${plan[0]!.n}~${plan[plan.length - 1]!.n}주차).`;
  return missing.length ? `${head} ${missing.join("·")}주차가 비어 있어요.` : head;
}
